import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { adminApi } from '../adminApi';
import { useAdminAuth } from '../adminAuth';
import { formatCost, formatInt } from '../format';
import { NeoScentBadges, type NeoSessionRow } from '../components/NeoScentBadges';

interface AdminUserDetail {
  user: {
    id: string;
    email: string;
    tier: string;
    email_verified: boolean;
    created_at: string;
    last_activity_at: string | null;
  };
  oauth_identities: Array<{ provider: string; subject: string; created_at: string }>;
  boats: Array<{
    id: string;
    display_name: string;
    created_at: string;
    last_contacted_at: string | null;
    deregistered_at: string | null;
  }>;
  totp_enrolled: boolean;
  admin_login_authorized: boolean;
  bridge_token_residual_seconds: number;
}

interface NeoSessionsResponse {
  rollup: { sessions: number; needsAttention: number; thumbsDown: number };
  sessions: NeoSessionRow[];
}

function formatDate(iso: string | null): string {
  if (iso === null) return '—';
  return new Date(iso).toLocaleString();
}

export function AdminUserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { admin } = useAdminAuth();
  const [data, setData] = useState<AdminUserDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [neo, setNeo] = useState<NeoSessionsResponse | null>(null);
  const [deleteArmed, setDeleteArmed] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteTriggerRef = useRef<HTMLButtonElement>(null);
  const confirmEmailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (id === undefined) return;
    void (async () => {
      try {
        const res = await adminApi<AdminUserDetail>(`/v1/admin/users/${id}`);
        setData(res);
      } catch (err) {
        const e = err as { status?: number; body?: { message?: string } };
        if (e.status === 404) {
          setError('User not found.');
        } else {
          setError(e.body?.message ?? 'Failed to load user.');
        }
      }
    })();
  }, [id]);

  useEffect(() => {
    if (deleteArmed) confirmEmailRef.current?.focus();
  }, [deleteArmed]);

  function armDeletion(): void {
    setConfirmEmail('');
    setDeleteError(null);
    setDeleteArmed(true);
  }

  function cancelDeletion(): void {
    setDeleteArmed(false);
    setConfirmEmail('');
    setDeleteError(null);
    window.requestAnimationFrame(() => deleteTriggerRef.current?.focus());
  }

  function handleDeleteKeys(event: KeyboardEvent<HTMLFormElement>): void {
    if (event.key !== 'Escape' || deleteBusy) return;
    event.preventDefault();
    cancelDeletion();
  }

  async function deleteUser(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (data === null || id === undefined || confirmEmail !== data.user.email || deleteBusy) return;

    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await adminApi(`/v1/admin/users/${id}`, {
        method: 'DELETE',
        body: { confirmEmail },
      });
      navigate('/admin', {
        replace: true,
        state: { notice: `Deleted ${data.user.email}.` },
      });
    } catch (err) {
      const apiError = err as { status?: number; body?: { error?: string; message?: string } };
      if (apiError.status === 404 && apiError.body?.error === 'not_found') {
        navigate('/admin', {
          replace: true,
          state: { notice: `${data.user.email} was already deleted.` },
        });
        return;
      }
      setDeleteError(
        apiError.body?.message ??
          (err instanceof Error ? err.message : 'Failed to delete the user. Try again.')
      );
    } finally {
      setDeleteBusy(false);
    }
  }

  // Neo chat sessions are a lens on the user, loaded independently so a failure
  // here never blanks the rest of the page.
  useEffect(() => {
    if (id === undefined) return;
    let ignore = false;
    adminApi<NeoSessionsResponse>(`/v1/admin/users/${id}/neo-sessions`)
      .then((res) => {
        if (!ignore) setNeo(res);
      })
      .catch(() => {
        /* leave the section empty on failure */
      });
    return () => {
      ignore = true;
    };
  }, [id]);

  return (
    <section className="admin-section">
      <p>
        <Link to="/admin">← Back to users</Link>
      </p>

      {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}
      {data === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {data !== null ? (
        <>
          <header className="admin-section-header">
            <h1>{data.user.email}</h1>
            <span className="admin-count">
              {data.user.tier} · {data.totp_enrolled ? '2FA enrolled' : 'no admin 2FA'}
            </span>
          </header>

          <dl className="admin-defs">
            <dt>User ID</dt>
            <dd>
              <code>{data.user.id}</code>
            </dd>
            <dt>Email verified</dt>
            <dd>{data.user.email_verified ? 'yes' : 'no'}</dd>
            <dt>Created</dt>
            <dd>{formatDate(data.user.created_at)}</dd>
            <dt>Last activity</dt>
            <dd>{formatDate(data.user.last_activity_at)}</dd>
          </dl>

          <h2>OAuth identities</h2>
          {data.oauth_identities.length === 0 ? (
            <p className="admin-empty">None.</p>
          ) : (
            <div className="scroll-x">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Subject</th>
                    <th>Linked</th>
                  </tr>
                </thead>
                <tbody>
                  {data.oauth_identities.map((o) => (
                    <tr key={`${o.provider}:${o.subject}`}>
                      <td>{o.provider}</td>
                      <td>
                        <code>{o.subject}</code>
                      </td>
                      <td>{formatDate(o.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h2>Boats</h2>
          {data.boats.length === 0 ? (
            <p className="admin-empty">No boats claimed.</p>
          ) : (
            <div className="scroll-x">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Boat ID</th>
                    <th>Created</th>
                    <th>Last contacted</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.boats.map((b) => (
                    <tr key={b.id}>
                      <td>
                        {b.display_name}
                        <Link
                          className="notebook-link"
                          to={`/admin/boats/${b.id}/notebook`}
                          state={{
                            boatName: b.display_name,
                            from: { to: `/admin/users/${data.user.id}`, label: data.user.email },
                          }}
                          aria-label={`Notebook for ${b.display_name}`}
                        >
                          Notebook
                        </Link>
                      </td>
                      <td>
                        <code>{b.id}</code>
                      </td>
                      <td>{formatDate(b.created_at)}</td>
                      <td>{formatDate(b.last_contacted_at)}</td>
                      <td>{b.deregistered_at === null ? 'active' : 'deregistered'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h2>
            Neo chat sessions
            {neo !== null ? (
              <span className="admin-count">
                {' '}
                {formatInt(neo.rollup.sessions)} session{neo.rollup.sessions === 1 ? '' : 's'}
                {neo.rollup.needsAttention > 0
                  ? ` · ${formatInt(neo.rollup.needsAttention)} need attention`
                  : ''}
              </span>
            ) : null}
          </h2>
          {neo === null ? (
            <p className="admin-loading-row">Loading…</p>
          ) : neo.sessions.length === 0 ? (
            <p className="admin-empty">No Neo chats.</p>
          ) : (
            <div className="scroll-x">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Last active</th>
                    <th>Turns</th>
                    <th>Cost</th>
                    <th>Signals</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {neo.sessions.map((s) => (
                    <tr key={s.id}>
                      <td>{formatDate(s.last_message_at)}</td>
                      <td>{formatInt(s.message_count)}</td>
                      <td>{formatCost(s.total_cost_usd)}</td>
                      <td>
                        <NeoScentBadges row={s} />
                      </td>
                      <td>
                        <Link to={`/admin/neo/sessions/${s.id}`}>Open transcript →</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <section className="admin-danger-zone" aria-labelledby="admin-user-danger-heading">
            <h2 id="admin-user-danger-heading">Danger zone</h2>
            <p>
              Permanently remove this account and its account-linked records. Linked boats and their
              data are retained.
            </p>

            {admin?.userId === data.user.id ? (
              <>
                <p id="admin-user-delete-blocked" className="admin-hint">
                  You cannot delete your own account from its current admin session. Another admin
                  must perform this action.
                </p>
                <button
                  type="button"
                  className="admin-button-danger"
                  aria-describedby="admin-user-delete-blocked"
                  disabled
                >
                  Delete user
                </button>
              </>
            ) : data.admin_login_authorized ? (
              <>
                <p id="admin-user-delete-blocked" className="admin-hint">
                  This email still has admin access. Remove it from ADMIN_ALLOWED_EMAILS before
                  deleting the account, or Google sign-in can recreate it.
                </p>
                <button
                  type="button"
                  className="admin-button-danger"
                  aria-describedby="admin-user-delete-blocked"
                  disabled
                >
                  Delete user
                </button>
              </>
            ) : deleteArmed ? (
              <form
                id="admin-user-delete-confirm"
                className="admin-delete-confirm"
                aria-labelledby="admin-user-delete-heading"
                aria-describedby="admin-user-delete-warning"
                aria-busy={deleteBusy}
                onSubmit={(event) => void deleteUser(event)}
                onKeyDown={handleDeleteKeys}
              >
                <h3 id="admin-user-delete-heading">Confirm permanent deletion</h3>
                <p id="admin-user-delete-warning" className="admin-delete-warning">
                  Permanently delete <strong>{data.user.email}</strong>? This removes the account
                  and its account-linked records.
                  {data.boats.length === 0
                    ? ' No boats are linked to this account.'
                    : data.boats.length === 1
                      ? ' It detaches the user from 1 linked boat. The boat and its data are retained; if this was its only owner, it becomes ownerless.'
                      : ` It detaches the user from ${data.boats.length} linked boats. The boats and their data are retained; any boat with no other owner becomes ownerless.`}{' '}
                  Previously issued boat bridge credentials can remain usable locally for up to{' '}
                  {Math.ceil(data.bridge_token_residual_seconds / 60)} minutes. This cannot be
                  undone.
                </p>
                <label htmlFor="admin-user-delete-email">
                  Type <code>{data.user.email}</code> to confirm
                </label>
                <input
                  ref={confirmEmailRef}
                  id="admin-user-delete-email"
                  type="email"
                  inputMode="email"
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={confirmEmail}
                  onChange={(event) => setConfirmEmail(event.target.value)}
                  disabled={deleteBusy}
                  aria-describedby="admin-user-delete-warning"
                />
                <div className="admin-delete-actions">
                  <button
                    type="button"
                    className="admin-button-secondary"
                    onClick={cancelDeletion}
                    disabled={deleteBusy}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="admin-button-danger"
                    data-testid={`admin-user-delete-confirm-${data.user.id}`}
                    disabled={deleteBusy || confirmEmail !== data.user.email}
                  >
                    {deleteBusy ? 'Deleting…' : 'Delete user permanently'}
                  </button>
                </div>
                {deleteError !== null ? (
                  <p className="admin-inline-error" role="alert">
                    {deleteError}
                  </p>
                ) : null}
              </form>
            ) : (
              <button
                ref={deleteTriggerRef}
                type="button"
                className="admin-button-danger"
                data-testid={`admin-user-delete-${data.user.id}`}
                onClick={armDeletion}
              >
                Delete user
              </button>
            )}
          </section>
        </>
      ) : null}
    </section>
  );
}
